import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import Loading from "../../components/Loading";
import { useActiveLibraryCategories } from "../../hooks/multi-library/useActiveLibraryCategories";
import { useActiveLibraryVideos } from "../../hooks/multi-library/useActiveLibraryVideos";
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { MULTI_LIBRARY } from "../../components/Routes/routingConstants";
import { Alert } from "reactstrap";
import { LibraryVideo } from "../../interfaces/models/LibraryVideo";
import { LibraryCategory } from "../../interfaces/models/LibraryCategory";
import LibraryCategoryComponent from "../../components/MultiLibrary/LibraryCategoryComponent";
import { useActiveLibraryCategory } from "../../hooks/multi-library/useActiveLibraryCategory";
import LibraryVideoComponent from "../../components/MultiLibrary/LibraryVideoComponent";


function MultiLibraryPage() {

  
  const query = new URLSearchParams(useLocation().search);
  const id = query.get("id");
  
  const { data: categoryData, refetch: categoryRefetch } = useActiveLibraryCategory(id);
  const { data: categoriesData, error: categoryError, loading: categoryLoading, refetch: categoriesRefetch } = useActiveLibraryCategories(id);
  const { data: videoData, error: videoError, loading: videoLoading, refetch: videoRefetch} = useActiveLibraryVideos(id);

  
  const [categoryBreadcrumbsData, setCategoryBreadcrumbsData] = useState<Array<{ name: string, path: string }>>([{ name: "Library", path: `${MULTI_LIBRARY}` }]);
  
  const [videos, setVideos] = useState<LibraryVideo[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refetchCategory = useCallback(
    async (id: string) => {
      return await categoriesRefetch({
        filter: {
          parent_id: id
        } 
      })
  } ,[categoriesRefetch]);

  const refetchVideo = useCallback(
    async (id: string) => {
      return await videoRefetch({
        filter: {
          library_category_id: id
        }
      })
  } ,[videoRefetch]);


  useEffect(() => {
    const rawCategory = categoryData?.library_category_active as LibraryCategory;
    if(rawCategory && id) {
      let crumbs = [{ name: "Library", path: `/${MULTI_LIBRARY}` }];
      for(let i = 0; i < rawCategory.parents.length; i++) {
        const parent = rawCategory.parents[i];
        crumbs.push({ name: parent.name, path: `/${MULTI_LIBRARY}?id=${parent._id}` });
      }
      crumbs.push({ name: rawCategory.name , path: `/${MULTI_LIBRARY}?id=${rawCategory._id}` });
      setCategoryBreadcrumbsData(crumbs);
    }
    else {
      setCategoryBreadcrumbsData([{ name: "Library", path: `${MULTI_LIBRARY}` }]);
    }
  }, [categoryData, id]);

  useEffect(() => {
    if (categoriesData) {
      setCategories(categoriesData.library_categories_active_only); 
    }
  }, [categoriesData]);

  useEffect(() => {
    if (videoData) {
      setVideos(videoData.library_videos_active_only);
    }
  }, [videoData]);

  useEffect(() => {
    const refetchData = async () => {
      setIsLoading(true);
      const categoriesData = await refetchCategory(id);
      const rawData = categoriesData?.data?.library_categories_active_only as LibraryCategory[] || [];
      if(rawData) {
        setCategories(rawData);
      }
      if(rawData.length === 0 && id) {
        const videoData = await refetchVideo(id); 
        if(videoData.data) {
          setVideos(videoData.data.library_videos_active_only);
        }
      }

      if(id) {
        await categoryRefetch({
          _id: id
        });
      }
      setIsLoading(false);
    }
    
    refetchData();
  }, [id, categoryRefetch, refetchCategory, refetchVideo]);

  return (
    <>
      <Breadcrumbs
      crumbs={categoryBreadcrumbsData}
    />

    {
      (categoryError || videoError) && <Alert color="warning" >Sorry! Error on getting library categories/videos</Alert>
    }

    {
      categoryLoading || videoLoading || isLoading ? 
        <Loading />
      :
        videos.length > 0 ?
        <LibraryVideoComponent 
          videos={videos}
        />
        :
        categories?.length > 0  && 
        <LibraryCategoryComponent 
          categories={categories}
          
        />
    }


    </>
  )
}

export { MultiLibraryPage as MultiLibrary };
